import { useState } from 'react';
import { UpgradeCta } from '@/features/billing/components/UpgradeCta';
import { useFeatureGate } from '@/features/billing/gates';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { Role } from '@/types/gql.generated';
import { Flex, FormControl, FormHelperText, Text } from '@/ui';
import { EditableLink } from './CopyLinkInput/EditableLink';
import { ReadOnlyLink } from './CopyLinkInput/ReadOnlyLink';

type Props = {
  hideAccessText?: boolean;
};

export const CopyLinkSection = ({ hideAccessText }: Props) => {
  const [isInputFocused, setIsInputFocused] = useState(false);

  const { experience } = usePageExperienceContext();
  // A custom URL is answered for by the org the experience is homed in, not by the
  // editing user's own Plus: the address outlives whoever typed it, so the workspace
  // left holding it is the one that has to be entitled to keep it.
  // `isLocked` keeps the input read-only until entitlement is known; `isGated` is
  // the narrower signal for whether an upgrade is worth offering.
  const { isGated: isSlugGated, isLocked: isSlugLocked } =
    useFeatureGate('customSlug');

  if (!experience) {
    return null;
  }

  // Setting the URL is the owner's call, like turning the public link on: it
  // outlives whoever typed it and the owner is the one left owning the address.
  // Collaborators still get the link to read and copy, which is why this is an
  // owner check here rather than `ownerOnly` on the gate — that reports `isHidden`
  // and would render nothing at all.
  const isOwner = experience.role === Role.Owner;
  const canEditSlug = isOwner && !isSlugLocked;
  // And no upgrade offer for a collaborator: they can't buy their way into a
  // decision that isn't theirs to make.
  const canUpgradeSlug = isOwner && isSlugGated;

  return (
    <Flex direction="column" gap="2">
      <Text fontWeight="medium">
        {isInputFocused
          ? 'Create your custom URL'
          : 'Use this URL to share with others!'}
      </Text>

      <FormControl>
        {canEditSlug ? (
          <EditableLink
            experience={experience}
            handleOnFocus={(isFocused) => setIsInputFocused(isFocused)}
            publishedSlug={experience.publishedSlug || ''}
          />
        ) : (
          <ReadOnlyLink experience={experience} />
        )}

        {(!hideAccessText || canUpgradeSlug) && (
          <Flex
            align="center"
            justifyContent={hideAccessText ? 'flex-end' : 'space-between'}
            pt={'10px'}
          >
            {!hideAccessText && (
              <FormHelperText color="currentColor">
                Anyone with the link{' '}
                <Text as="span" fontWeight="bold">
                  can view
                </Text>
              </FormHelperText>
            )}

            {canUpgradeSlug && (
              <Flex mt="10px">
                <UpgradeCta
                  feature="customSlug"
                  label="Upgrade for custom URL"
                />
              </Flex>
            )}
          </Flex>
        )}
      </FormControl>
    </Flex>
  );
};
